import { createElement, useCallback, useRef } from "react";
import { useInView } from "@hooks/useInView";
import type { RevealProps, RevealDirection } from "./Reveal.types";

const EXPO_OUT = "cubic-bezier(0.16, 1, 0.3, 1)";

function getTranslate(direction: RevealDirection, distance: number): string {
  switch (direction) {
    case "up":
      return `translate3d(0, ${distance}px, 0)`;
    case "down":
      return `translate3d(0, -${distance}px, 0)`;
    case "left":
      return `translate3d(${distance}px, 0, 0)`;
    case "right":
      return `translate3d(-${distance}px, 0, 0)`;
    case "none":
      return "translate3d(0, 0, 0)";
  }
}

export function Reveal({
  children,
  direction = "up",
  delay = 0,
  duration = 700,
  distance = 30,
  initialScale,
  as = "div",
  threshold = 0.15,
  className,
}: RevealProps) {
  const { ref, isInView } = useInView({ threshold });
  const hasAnimated = useRef(false);

  // Track animation completion to reset will-change
  const handleTransitionEnd = useCallback(() => {
    hasAnimated.current = true;
  }, []);

  const hidden = !isInView;
  const scale = initialScale ?? 1;

  const style: React.CSSProperties = {
    opacity: hidden ? 0 : 1,
    transform: hidden
      ? `${getTranslate(direction, distance)} scale(${scale})`
      : "translate3d(0, 0, 0) scale(1)",
    transition: `opacity ${duration}ms ${EXPO_OUT} ${delay}ms, transform ${duration}ms ${EXPO_OUT} ${delay}ms`,
    willChange: hasAnimated.current ? "auto" : "opacity, transform",
  };

  return createElement(
    as,
    {
      ref,
      style,
      className,
      onTransitionEnd: handleTransitionEnd,
    },
    children
  );
}
